import { produce } from 'immer';
import omit from 'lodash/omit';
import set from 'lodash/set';
import { any, bool, func, number, shape } from 'prop-types';
import React from 'react';

import { guardShape, withGuard } from '../generic/change-guard';
import { historyShape } from '../prop-types';
import getDisplayName from '../utils/get-display-name';
import getEventTargetValue from '../utils/get-event-target-value';
import withApiRequest from './with-api-request';

const OWN_PROPERTIES = [
  'default',
  'onRequest',
  'history',
  'loading',
  'response',
  'error',
  'guard',
];

function getFormProps(props) {
  return omit(props, OWN_PROPERTIES);
}

export default function addFactory({
  update,
  form: Form,
  default: defaultState,
  redirectTo,
}) {
  class AddComponent extends React.Component {
    constructor(props) {
      super(props);

      this.savedState = this.getDefaultState();
      this.state = this.savedState;

      this.handleChange = this.handleChange.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleDiscard = this.handleDiscard.bind(this);
    }

    handleChange(event) {
      const { target } = event;
      const { name } = target;
      const value = getEventTargetValue(target);
      if (!(name.includes('.') || name.includes('['))) {
        this.setState({ [name]: value });
      } else {
        this.setState(state =>
          produce(state, mutableState => set(mutableState, name, value)),
        );
      }
      this.props.guard.mark();
    }

    handleSubmit(event) {
      event.preventDefault();
      event.stopPropagation();
      const { onRequest, loading } = this.props;

      if (loading) {
        // Only make one request at a time
        return Promise.reject(
          new Error('The previous request is in progress.'),
        );
      }

      const formProps = getFormProps(this.props);
      const sentState = this.state;

      return onRequest(formProps, sentState, this.savedState).then(() => {
        const { guard, history, response, onSuccess } = this.props;

        // Only reset guard modified state if the current form state still
        // corresponds to the one we just successfully sent.
        if (sentState === this.state) guard.reset();

        this.savedState = sentState;

        if (redirectTo && response && history) {
          if (redirectTo.indexOf(':accountId') > -1)
            history.push(redirectTo.replace(':accountId', response.accountId));
          else history.push(redirectTo.replace(':id', response.id));
        } else if (onSuccess) {
          onSuccess(response);
        }
      });
    }

    handleDiscard() {
      this.setState(this.savedState);
      this.props.guard.reset();
    }

    getDefaultState() {
      return defaultState || this.props.default;
    }

    render() {
      const formProps = getFormProps(this.props);

      return (
        <Form
          {...formProps}
          model={this.state}
          onSubmit={this.handleSubmit}
          onChange={this.handleChange}
          onDiscard={this.handleDiscard}
        />
      );
    }
  }

  AddComponent.displayName = `add(${getDisplayName(Form)})`;

  AddComponent.propTypes = {
    onRequest: func.isRequired,
    history: historyShape,
    loading: bool.isRequired,
    default: defaultState ? any : any.isRequired, // eslint-disable-line react/forbid-prop-types
    response: shape({ id: number.isRequired }),
    guard: guardShape.isRequired,
    onSuccess: func,
  };

  AddComponent.defaultProps = {
    default: undefined,
    response: undefined,
    history: undefined,
    onSuccess: undefined,
  };

  return withApiRequest({ query: update, renderLoading: true, error: 'flash' })(
    withGuard(AddComponent),
  );
}
